// src/components/promos/CodeBatchModal.tsx
import { useState } from "react";
import Modal from "@/components/shared/ui/Modal";
import Input from "@/components/shared/ui/Input";
import { useGeneratePromoCodesMutation } from "@/api/features/promos";
import type { Promo } from "@/api/features/promos";

export default function CodeBatchModal({ promo, onClose }: { promo: Promo; onClose: () => void }) {
  const [count, setCount] = useState("100");
  const [prefix, setPrefix] = useState("");
  const [length, setLength] = useState("10");
  const [maxUsesPerCode, setMaxUsesPerCode] = useState("1");
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const gen = useGeneratePromoCodesMutation(promo.id);

  const run = () => {
    setError(null);
    setCodes(null);
    gen.mutate(
      {
        count: Number(count) || 0,
        prefix: prefix.trim() || undefined,
        length: Number(length) || 10,
        maxUsesPerCode: Number(maxUsesPerCode) || 1,
      },
      {
        onSuccess: (r) => setCodes(r.codes),
        onError: (e) => setError(e.message || "Generation failed"),
      }
    );
  };

  const downloadCsv = () => {
    if (!codes?.length) return;
    const blob = new Blob([["code", ...codes].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `promo-${promo.id}-codes.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Modal open title={`Generate codes — ${promo.name}`} onClose={onClose}>
      <div className="space-y-4">
        {promo.codeMode !== "unique_batch" ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
            This promo uses a shared code — batch generation is only for unique-batch promos.
          </div>
        ) : null}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">How many</span>
            <Input type="number" min="1" max="5000" value={count} onChange={(e) => setCount(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Prefix (optional)</span>
            <Input value={prefix} maxLength={16} onChange={(e) => setPrefix(e.target.value)} placeholder="SUMMER" />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Random length</span>
            <Input type="number" min="4" max="24" value={length} onChange={(e) => setLength(e.target.value)} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-gray-700">Max uses per code</span>
            <Input type="number" min="1" value={maxUsesPerCode} onChange={(e) => setMaxUsesPerCode(e.target.value)} />
          </label>
        </div>

        {error ? (
          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">{error}</div>
        ) : null}

        {codes ? (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-emerald-700">Generated {codes.length} codes</span>
              <button onClick={downloadCsv} className="rounded-md border border-gray-300 px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50">
                Download CSV
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-gray-50 p-2 font-mono text-[11px] text-gray-700">
              {codes.slice(0, 200).map((c) => (
                <div key={c}>{c}</div>
              ))}
              {codes.length > 200 ? <div className="text-gray-400">… download CSV for all {codes.length}</div> : null}
            </div>
          </div>
        ) : null}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Close</button>
          <button
            onClick={run}
            disabled={gen.isPending || promo.codeMode !== "unique_batch"}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
          >
            {gen.isPending ? "Generating…" : "Generate"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
